import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { LogOut, Calendar, Phone, Mail, RefreshCw } from 'lucide-react';
import { serviceLabels } from '../mock';

const API = process.env.REACT_APP_BACKEND_URL;
const HTTP_UNAUTHORIZED = 401;

const STATUS_CONFIG = {
  nueva: { label: 'Nueva', className: 'bg-amber-100 text-amber-800 border-amber-200' },
  contactada: { label: 'Contactada', className: 'bg-blue-100 text-blue-800 border-blue-200' },
  agendada: { label: 'Cita agendada', className: 'bg-green-100 text-green-800 border-green-200' }
};

const formatDate = (iso) => new Date(iso).toLocaleString('es-EC', { dateStyle: 'medium', timeStyle: 'short' });

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Algo salió mal. Inténtalo de nuevo.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
  return String(detail);
}

const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password }, { withCredentials: true });
      onLogin(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-50 flex items-center justify-center px-4">
      <Card className="w-full max-w-md border-sage-100 shadow-xl">
        <CardHeader className="text-center">
          <h1 className="text-4xl font-serif font-bold text-[#6E4F35] mb-2">Qara</h1>
          <CardTitle className="text-lg font-normal text-gray-600">Panel de Citas</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="admin-email">Correo</Label>
              <Input
                id="admin-email"
                type="email"
                data-testid="admin-login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="correo@ejemplo.com"
                className="border-sage-200"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password">Contraseña</Label>
              <Input
                id="admin-password"
                type="password"
                data-testid="admin-login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="border-sage-200"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit-button"
              className="w-full bg-[#6E4F35] hover:bg-[#5a3f2a] text-cream-100 py-5 rounded-xl"
            >
              {loading ? 'Ingresando...' : 'Ingresar'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const RequestCard = ({ req, onUpdateStatus }) => {
  const statusConfig = STATUS_CONFIG[req.status] || STATUS_CONFIG.nueva;
  const currentStatus = req.status || 'nueva';

  return (
    <Card className="border-sage-100 hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-[200px]">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-gray-900 text-lg">{req.name}</p>
              <span
                data-testid={`status-badge-${req.id}`}
                className={`text-xs font-medium px-2.5 py-0.5 rounded-full border ${statusConfig.className}`}
              >
                {statusConfig.label}
              </span>
            </div>
            <p className="text-sm text-[#6E4F35] font-medium">{serviceLabels[req.service] || req.service || 'Consulta General'}</p>
            <p className="text-xs text-gray-400 mt-1">Recibida: {formatDate(req.timestamp)}</p>
          </div>
          <div className="text-sm text-gray-600 space-y-1">
            <p className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-sage-700" />
              <a href={`https://wa.me/${req.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="hover:text-[#25D366]">{req.phone}</a>
            </p>
            <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-sage-700" /> {req.email}</p>
            {req.preferred_date && (
              <p className="flex items-center gap-2"><Calendar className="w-4 h-4 text-sage-700" /> Fecha preferida: {req.preferred_date}</p>
            )}
          </div>
        </div>
        {req.message && (
          <p className="mt-3 text-sm text-gray-600 bg-sage-50 rounded-lg p-3">{req.message}</p>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            currentStatus !== key && (
              <Button
                key={key}
                size="sm"
                variant="outline"
                data-testid={`set-status-${key}-${req.id}`}
                onClick={() => onUpdateStatus(req.id, key)}
                className="border-sage-200 text-gray-700 hover:bg-sage-50 text-xs"
              >
                Marcar {cfg.label.toLowerCase()}
              </Button>
            )
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const RequestsList = ({ loading, requests, onUpdateStatus }) => {
  if (loading) {
    return <p className="text-gray-500">Cargando...</p>;
  }
  if (requests.length === 0) {
    return (
      <Card className="border-sage-100">
        <CardContent className="p-10 text-center text-gray-500">Aún no hay solicitudes de citas.</CardContent>
      </Card>
    );
  }
  return (
    <div className="grid gap-4" data-testid="admin-requests-list">
      {requests.map((req) => (
        <RequestCard key={req.id} req={req} onUpdateStatus={onUpdateStatus} />
      ))}
    </div>
  );
};

const AdminDashboard = ({ user, onLogout }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/contact`, { withCredentials: true });
      setRequests(data);
    } catch (err) {
      if (err.response?.status === HTTP_UNAUTHORIZED) onLogout();
      else toast.error('Error cargando solicitudes');
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error('Error cerrando sesión:', err);
    }
    onLogout();
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/api/contact/${id}/status`, { status }, { withCredentials: true });
      setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`Solicitud marcada como "${STATUS_CONFIG[status].label}"`);
    } catch (err) {
      if (err.response?.status === HTTP_UNAUTHORIZED) onLogout();
      else toast.error('Error actualizando el estado');
    }
  };

  return (
    <div className="min-h-screen bg-cream-50">
      <header className="bg-white border-b border-sage-100 sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-serif font-bold text-[#6E4F35]">Qara — Panel de Citas</h1>
            <p className="text-sm text-gray-500">{user.name}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={fetchRequests} data-testid="admin-refresh-button" className="border-sage-200">
              <RefreshCw className="w-4 h-4 mr-1" /> Actualizar
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="admin-logout-button" className="border-sage-200 text-red-700">
              <LogOut className="w-4 h-4 mr-1" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-800">
            Solicitudes recibidas
            <span data-testid="admin-requests-count" className="ml-2 text-sm font-normal text-gray-500">({requests.length})</span>
          </h2>
        </div>
        <RequestsList loading={loading} requests={requests} onUpdateStatus={updateStatus} />
      </main>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/auth/me`, { withCredentials: true })
      .then(({ data }) => setUser(data))
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return <div className="min-h-screen bg-cream-50 flex items-center justify-center text-gray-500">Cargando...</div>;
  }

  return (
    <>
      {user ? <AdminDashboard user={user} onLogout={() => setUser(false)} /> : <LoginForm onLogin={setUser} />}
      <Toaster position="top-center" richColors />
    </>
  );
}
